import type { StageDateEntry } from './hooks/useStageDateEditing';

export type StageLike = {
  stage_name: string;
  construction_stage_key?: string | null;
};

export type StageCreate = {
  stageName: string;
  payload: {
    inspection_id: string;
    site_id: string;
    stage_name: string;
    canonical_stage: string;
    start_date_noted: string | null;
    completion_date_noted: string | null;
    confidence_score: number;
    extraction_method: 'manual';
  };
};

const toDatetime = (d: string | undefined) => (d ? `${d}T00:00:00` : null);

/**
 * Stage rows the submit (or Edit → Review) must create: every schema stage the
 * inspector dated that has no observation row yet. Rows that already exist are
 * saved as the inspector types (the hook PATCHes them), so they are skipped
 * here. Runs on revise too — a stage first dated during an edit used to be
 * dropped on the floor and the regenerated report showed the old table
 * (Chris, staging 2026-09-17).
 */
export function planStageCreates(
  stageDates: Record<string, StageDateEntry>,
  stages: StageLike[],
  inspectionId: string,
  siteId: string
): StageCreate[] {
  const out: StageCreate[] = [];
  for (const stage of stages) {
    const entry = stageDates[stage.stage_name];
    if (!entry || (!entry.start && !entry.completion) || entry.extractedStageId)
      continue;
    out.push({
      stageName: stage.stage_name,
      payload: {
        inspection_id: inspectionId,
        site_id: siteId,
        stage_name: stage.stage_name,
        canonical_stage:
          stage.construction_stage_key ||
          stage.stage_name.toLowerCase().replace(/\s+/g, '_'),
        start_date_noted: toDatetime(entry.start),
        completion_date_noted: toDatetime(entry.completion),
        confidence_score: 1.0,
        extraction_method: 'manual',
      },
    });
  }
  return out;
}

/**
 * Remember the ids the server handed back so a later edit of the same stage
 * PATCHes that row instead of being lost (or, on revise, duplicated).
 */
export function applyStageIds(
  stageDates: Record<string, StageDateEntry>,
  created: Array<{ stageName: string; id: string }>
): Record<string, StageDateEntry> {
  if (created.length === 0) return stageDates;
  const next = { ...stageDates };
  for (const { stageName, id } of created) {
    const entry = next[stageName];
    if (entry) next[stageName] = { ...entry, extractedStageId: id };
  }
  return next;
}
